import type { TerminalConfigurationResponse } from '@/services/revenue/functions';
import type { ReaderOption } from '@/hooks/useStripeTerminalSession';

export const TERMINAL_LAST_READER_STORAGE_KEY = 'rodeonails.terminal.lastReaderId';

export const TERMINAL_UNEXPECTED_DISCONNECT_MESSAGE =
  'Card reader disconnected unexpectedly. Reconnecting…';

export const TERMINAL_RECONNECT_FAILED_MESSAGE =
  'Unable to reconnect to the card reader. Use Connect reader to try again.';

export const MAX_TERMINAL_RECONNECT_ATTEMPTS = 8;

export function getLastConnectedReaderId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage.getItem(TERMINAL_LAST_READER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveLastConnectedReaderId(readerId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(TERMINAL_LAST_READER_STORAGE_KEY, readerId);
  } catch {
    // Ignore storage failures in private browsing or restricted contexts.
  }
}

export function clearLastConnectedReaderId() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(TERMINAL_LAST_READER_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export function getTerminalReconnectDelayMs(attempt: number): number {
  const normalizedAttempt = Math.max(attempt, 0);
  return Math.min(1000 * 2 ** normalizedAttempt, 30_000);
}

export function pickTerminalReader(
  discoveredReaders: ReaderOption[],
  terminalConfig: TerminalConfigurationResponse | undefined,
  preferredReaderId?: string | null,
): ReaderOption | undefined {
  if (discoveredReaders.length === 0) {
    return undefined;
  }

  const preferredIds = [
    preferredReaderId?.trim(),
    terminalConfig?.readers.find((reader) => reader.readerId)?.readerId,
  ].filter((readerId): readerId is string => Boolean(readerId));

  for (const readerId of preferredIds) {
    const matchedReader = discoveredReaders.find((reader) => reader.id === readerId);
    if (matchedReader) {
      return matchedReader;
    }
  }

  return discoveredReaders[0];
}
